import {
  GetAgenticLogicRuleDraftRequestPathParamsType,
  GetAgenticLogicRuleDraftResponseBodyType,
} from "@intelligems/ig-types";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";

/**
 * Exported so the one caller that must read the draft OUTSIDE a component
 * render — the Atlas chat's creation-context builder, which grounds an edit
 * session on the rule as it stands — reuses this fetcher rather than
 * re-writing the URL.
 */
export const getAgenticLogicRuleDraft = async (
  pathParams: GetAgenticLogicRuleDraftRequestPathParamsType
) => {
  const resp = await api.get<GetAgenticLogicRuleDraftResponseBodyType>(
    `/v2/agentic-logic/rules/${pathParams.ruleId}/draft`
  );
  return resp.data;
};

/**
 * Reads the rule's working draft. `draft.source` is returned INLINE — unlike
 * the shipping-provider draft there is no presigned URL to fetch separately —
 * and is null on a rule that has never been saved.
 *
 * `refetchInterval` lets an additional observer poll the shared key while an
 * out-of-band writer (the Atlas chat's backend save, the SQS graph-regen
 * worker) is expected to land a draft; TanStack runs the query at the
 * smallest interval among its observers, so plain readers are unaffected.
 */
export const useAgenticLogicRuleDraft = (
  ruleId: string | undefined,
  options?: { refetchInterval?: number | false | (() => number | false) }
) => {
  return useQuery<GetAgenticLogicRuleDraftResponseBodyType, Error>({
    queryKey: agenticLogicKeys.draft(ruleId!),
    queryFn: async () => getAgenticLogicRuleDraft({ ruleId: ruleId! }),
    enabled: !!ruleId,
    refetchInterval: options?.refetchInterval,
  });
};
